/**
 * Anlagen-Archiv (#780): die in der App stillgelegten Geräte.
 *
 * Beim Stilllegen löst der Server das Gerät von seiner Etage (`floor`/`space`/`x`/`y` werden
 * geleert) — genau deshalb verschwindet es aus Etagenliste, Etagenplan und Objektübersicht.
 * Gelöscht wird nichts, und für den Nachweis (Compliance) muss es weiter einsehbar bleiben:
 * dieses Panel ist dieser Zugang. Es liest ausschließlich; wieder in Betrieb genommen wird ein
 * Gerät in der App, nicht hier.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAtom } from "jotai";
import { Archive, MapPin, RefreshCw } from "lucide-react";

import type { ColumnDef } from "@tanstack/react-table";

import { authenticationAtom } from "../State";
import { useAssetArchiveAPI, type ArchivedAsset } from "../AssetArchiveAPI";
import {
  describeArchivedLocation,
  resolveArchivedLocations,
  type ArchivedLocationParts,
} from "../utilities/archivedLocation";

import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { DataTable } from "@/components/ui/data-table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const formatDateTime = (value: any): string => {
  if (!value) return "—";
  const date = new Date(value);

  return isNaN(date.getTime()) ? "—" : date.toLocaleString();
};

export default function AssetArchivePanel() {
  const { t } = useTranslation();
  const [authentication] = useAtom(authenticationAtom) as any;
  const customer = authentication?.customer;
  const archiveAPI = useAssetArchiveAPI(customer ?? "");

  const [assets, setAssets] = useState<ArchivedAsset[]>([]);
  const [locations, setLocations] = useState<Record<string, ArchivedLocationParts>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | undefined>(undefined);
  const [selected, setSelected] = useState<ArchivedAsset | undefined>(undefined);

  const load = useCallback(async () => {
    if (!customer) return;

    setLoading(true);
    setError(undefined);

    try {
      const archived = await archiveAPI.getArchivedAssets();
      setAssets(archived);

      // Der Standort ist als Id archiviert. Für den Nachweis zählt aber das Objekt, also
      // Liegenschaft und Gebäude — die hängen an der Etage, nicht am Gerät. Ein
      // fehlgeschlagener Lookup darf die Liste selbst nicht verhindern.
      setLocations(await resolveArchivedLocations(archived).catch(() => ({})));
    } catch (loadError: any) {
      setError(loadError?.message ?? String(loadError));
      setAssets([]);
    } finally {
      setLoading(false);
    }
  }, [customer]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    void load();
  }, [load]);

  const describeLocation = useCallback(
    (asset: ArchivedAsset): string =>
      describeArchivedLocation(asset, locations[String(asset._id)]),
    [locations],
  );

  const columns = useMemo<ColumnDef<ArchivedAsset>[]>(
    () => [
      {
        accessorKey: "name",
        header: t("assetArchivePanel.columns.device"),
        cell: ({ row }) => (
          <div className="flex flex-col">
            <span className="font-medium">{row.original.name || row.original.id || "—"}</span>
            {row.original.id && row.original.name ? (
              <span className="text-xs text-muted-foreground">{row.original.id}</span>
            ) : null}
          </div>
        ),
      },
      {
        accessorKey: "schemaCategory",
        header: t("assetArchivePanel.columns.category"),
        cell: ({ row }) => row.original.schemaCategory || "—",
      },
      {
        accessorKey: "decommissionedAt",
        header: t("assetArchivePanel.columns.decommissionedAt"),
        cell: ({ row }) => formatDateTime(row.original.decommissionedAt),
      },
      {
        id: "previousLocation",
        header: t("assetArchivePanel.columns.previousLocation"),
        cell: ({ row }) => (
          <span className="inline-flex items-center gap-1">
            <MapPin className="h-3.5 w-3.5 text-muted-foreground" aria-hidden />
            {describeLocation(row.original)}
          </span>
        ),
      },
    ],
    [t, describeLocation],
  );

  if (!customer) {
    return null;
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-start justify-between gap-4">
        <p className="max-w-3xl text-sm text-muted-foreground">
          {t("assetArchivePanel.description")}
        </p>
        <Button variant="outline" size="sm" onClick={() => void load()} disabled={loading}>
          <RefreshCw className="mr-2 h-4 w-4" aria-hidden />
          {t("assetArchivePanel.refresh")}
        </Button>
      </div>

      {error ? (
        <Alert variant="destructive">
          <AlertDescription>{t("assetArchivePanel.loadError", { error })}</AlertDescription>
        </Alert>
      ) : null}

      <DataTable
        columns={columns}
        data={assets}
        loading={loading}
        onRowClick={(asset) => setSelected(asset)}
        emptyMessage={
          <span className="inline-flex items-center gap-2">
            <Archive className="h-4 w-4" aria-hidden />
            {t("assetArchivePanel.empty")}
          </span>
        }
      />

      <Dialog open={!!selected} onOpenChange={(open) => !open && setSelected(undefined)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{selected?.name || selected?.id || ""}</DialogTitle>
            <DialogDescription>
              {t("assetArchivePanel.detail.subtitle", {
                at: formatDateTime(selected?.decommissionedAt),
              })}
            </DialogDescription>
          </DialogHeader>

          <dl className="grid grid-cols-[max-content_1fr] gap-x-6 gap-y-1 text-sm">
            <dt className="text-muted-foreground">{t("assetArchivePanel.columns.device")}</dt>
            <dd>{selected?.id || "—"}</dd>
            <dt className="text-muted-foreground">{t("assetArchivePanel.columns.category")}</dt>
            <dd>{selected?.schemaCategory || "—"}</dd>
            <dt className="text-muted-foreground">
              {t("assetArchivePanel.columns.previousLocation")}
            </dt>
            <dd>{selected ? describeLocation(selected) : "—"}</dd>
          </dl>

          <div className="flex flex-col gap-2">
            <h3 className="text-sm font-medium">{t("assetArchivePanel.detail.history")}</h3>
            {(selected?._stateHistory ?? []).length === 0 ? (
              <p className="text-sm text-muted-foreground">
                {t("assetArchivePanel.detail.noHistory")}
              </p>
            ) : (
              <ul className="flex flex-col gap-2">
                {(selected?._stateHistory ?? []).map((change, index) => (
                  <li
                    key={`${change.changedAt ?? index}-${index}`}
                    className="rounded-md border border-border/60 p-2 text-sm"
                  >
                    <div className="flex flex-wrap items-baseline justify-between gap-2">
                      <span className="font-medium">
                        {t("assetArchivePanel.detail.transition", {
                          from: change.from ?? "—",
                          to: change.to ?? "—",
                        })}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        {formatDateTime(change.changedAt)}
                      </span>
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {t("assetArchivePanel.detail.changedBy", {
                        actor: change.changedBy ?? "—",
                      })}
                    </div>
                    {change.reason ? <div className="text-xs">{change.reason}</div> : null}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
